import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Header } from './components/layout/Header';
import { UserHeader } from './components/layout/UserHeader';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { Recuperar } from './pages/Recuperar';
import { Reset } from './pages/Reset';
import { Consultas } from './pages/Consultas';
import Configuracion from './pages/Configuracion';
import MisMascotas from './pages/user/MisMascotas';
import Agenda from './pages/user/Agenda';
import Socios from './pages/user/Socios';
import QRMascota from './pages/user/QRMascota';
import MascotaProtegida from './pages/MascotaProtegida';
import About from './pages/About';
import ContactoPage from './pages/Contacto';
import Comprar from './pages/Comprar';

// ──────────────── ADMIN ────────────────
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import UsuariosPanel from './pages/admin/paneles/UsuariosPanel';
import MascotasPanel from './pages/admin/paneles/MascotasPanel';
import CodigosPanel from './pages/admin/paneles/CodigosPanel';
import UbicacionesPanel from './pages/admin/paneles/UbicacionesPanel';
import ContactosPanel from './pages/admin/paneles/ContactosPanel';
import HistorialPanel from './pages/admin/paneles/HistorialPanel';
import SociosPanel from './pages/admin/paneles/SociosPanel';
import ProductosPanel from './pages/admin/paneles/ProductosPanel';

import ProtectedRoute from './components/ProtectedRoute';
import EstadisticasPanel from './pages/admin/estadisticas/EstadisticasPanel';

// ─── Placeholder ───
const Placeholder = ({ title }) => (
  <main className="min-h-screen bg-sp-light flex items-center justify-center px-4">
    <div className="text-center">
      <h1 className="text-sp-primary font-black text-2xl mb-2">{title}</h1>
      <p className="text-sp-muted font-semibold">Próximamente</p>
    </div>
  </main>
);

// ═══════════════════════════════════════════════════
// HEADER DINÁMICO
// ═══════════════════════════════════════════════════
function AppHeader() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const noHeaderPaths = [
    '/login',
    '/register',
    '/recuperar',
    '/recuperar/reset',
    '/mascotaprotegida',
    '/admin', // admin tiene su propio layout, no necesita header
  ];

  const path = location.pathname.toLowerCase();
  if (noHeaderPaths.some((p) => path.startsWith(p))) {
    return null;
  }

  return isAuthenticated ? <UserHeader /> : <Header />;
}

// ═══════════════════════════════════════════════════
// RUTAS
// ═══════════════════════════════════════════════════
function AppRoutes() {
  return (
    <>
      <AppHeader />
      <Routes>
        {/* ── PÚBLICAS ── */}
        <Route path="/" element={<Home />} />

        {/* Autenticación */}
        <Route path="/login" element={<Login />} />
        <Route path="/Login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/Register" element={<Register />} />
        <Route path="/recuperar" element={<Recuperar />} />
        <Route path="/Recuperar" element={<Recuperar />} />
        <Route path="/recuperar/reset" element={<Reset />} />
        <Route path="/Recuperar/Reset" element={<Reset />} />

        {/* Páginas institucionales */}
        <Route path="/about" element={<About />} />
        <Route path="/About" element={<About />} />
        <Route path="/comprar" element={<Comprar />} />
        <Route path="/Comprar" element={<Comprar />} />
        <Route path="/contacto" element={<ContactoPage />} />
        <Route path="/Contacto" element={<ContactoPage />} />

        {/* Mascota protegida (pública) */}
        <Route path="/mascotaProtegida/:id" element={<MascotaProtegida />} />
        <Route path="/MascotaProtegida/:id" element={<MascotaProtegida />} />
        <Route path="/mascotaprotegida/:id" element={<MascotaProtegida />} />

        {/* ── USUARIO LOGUEADO ── */}
        <Route path="/consultas/:id" element={<ProtectedRoute><Consultas /></ProtectedRoute>} />
        <Route path="/Consultas/:id" element={<ProtectedRoute><Consultas /></ProtectedRoute>} />

        <Route path="/consultas/:id/configuracion" element={<ProtectedRoute><Configuracion /></ProtectedRoute>} />
        <Route path="/Consultas/:id/configuracion" element={<ProtectedRoute><Configuracion /></ProtectedRoute>} />

        <Route path="/consultas/:userId/mismascotas" element={<ProtectedRoute><MisMascotas /></ProtectedRoute>} />
        <Route path="/Consultas/:userId/mismascotas" element={<ProtectedRoute><MisMascotas /></ProtectedRoute>} />

        <Route path="/consultas/:userId/agenda" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
        <Route path="/Consultas/:userId/agenda" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />

        <Route path="/consultas/:userId/socios" element={<ProtectedRoute><Socios /></ProtectedRoute>} />
        <Route path="/Consultas/:userId/socios" element={<ProtectedRoute><Socios /></ProtectedRoute>} />

        <Route path="/consultas/:userId/qr/:mascotaId" element={<ProtectedRoute><QRMascota /></ProtectedRoute>} />
        <Route path="/Consultas/:userId/qr/:mascotaId" element={<ProtectedRoute><QRMascota /></ProtectedRoute>} />

        {/* ── LEGACY REDIRECTS ── */}
        <Route path="/misMascotas/:id" element={<ProtectedRoute><Navigate to={`/consultas/${JSON.parse(localStorage.getItem('user') || '{}').id}/mismascotas`} replace /></ProtectedRoute>} />
        <Route path="/MisMascotas/:id" element={<ProtectedRoute><Navigate to={`/consultas/${JSON.parse(localStorage.getItem('user') || '{}').id}/mismascotas`} replace /></ProtectedRoute>} />

        <Route path="/agenda/:userId" element={<ProtectedRoute><Navigate to={`/consultas/${JSON.parse(localStorage.getItem('user') || '{}').id}/agenda`} replace /></ProtectedRoute>} />
        <Route path="/Agenda/:userId" element={<ProtectedRoute><Navigate to={`/consultas/${JSON.parse(localStorage.getItem('user') || '{}').id}/agenda`} replace /></ProtectedRoute>} />

        <Route path="/configuracion" element={<ProtectedRoute><Navigate to={`/consultas/${JSON.parse(localStorage.getItem('user') || '{}').id}/configuracion`} replace /></ProtectedRoute>} />

        <Route path="/socios/:userId" element={<ProtectedRoute><Navigate to={`/consultas/${JSON.parse(localStorage.getItem('user') || '{}').id}/socios`} replace /></ProtectedRoute>} />
        <Route path="/Socios" element={<ProtectedRoute><Navigate to={`/consultas/${JSON.parse(localStorage.getItem('user') || '{}').id}/socios`} replace /></ProtectedRoute>} />

        {/* ═══════════════════════════════════════════════════
            ADMIN (protegido con adminOnly)
           ═══════════════════════════════════════════════════ */}
        <Route path="/admin" element={<ProtectedRoute adminOnly><Navigate to="/admin/dashboard" replace /></ProtectedRoute>} />
        <Route path="/admin/dashboard" element={<ProtectedRoute adminOnly><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
        <Route path="/admin/usuarios" element={<ProtectedRoute adminOnly><AdminLayout><UsuariosPanel /></AdminLayout></ProtectedRoute>} />
        <Route path="/admin/mascotas" element={<ProtectedRoute adminOnly><AdminLayout><MascotasPanel /></AdminLayout></ProtectedRoute>} />
        <Route path="/admin/codigos" element={<ProtectedRoute adminOnly><AdminLayout><CodigosPanel /></AdminLayout></ProtectedRoute>} />
        <Route path="/admin/ubicaciones" element={<ProtectedRoute adminOnly><AdminLayout><UbicacionesPanel /></AdminLayout></ProtectedRoute>} />
        <Route path="/admin/contactos" element={<ProtectedRoute adminOnly><AdminLayout><ContactosPanel /></AdminLayout></ProtectedRoute>} />
        <Route path="/admin/historial" element={<ProtectedRoute adminOnly><AdminLayout><HistorialPanel /></AdminLayout></ProtectedRoute>} />
        <Route path="/admin/socios" element={<ProtectedRoute adminOnly><AdminLayout><SociosPanel /></AdminLayout></ProtectedRoute>} />
        <Route path="/admin/productos" element={<ProtectedRoute adminOnly><AdminLayout><ProductosPanel /></AdminLayout></ProtectedRoute>} />
        <Route path="/admin/estadisticas" element={<ProtectedRoute adminOnly><AdminLayout><EstadisticasPanel /></AdminLayout></ProtectedRoute>} />

        {/* ── 404 ── */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// ═══════════════════════════════════════════════════
// APP
// ═══════════════════════════════════════════════════
function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;